import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import InventoryIcon from '@mui/icons-material/Inventory';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import ReceiptIcon from '@mui/icons-material/Receipt';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import pedidoVentaService, { PedidoVenta } from '../../services/pedidoVentaService';
import PedidoDetalle from './components/PedidoDetalle';
import PDFViewer from '../../components/shared/PDFViewer';
import { formatCurrency } from '../../utils/formatters';
import { getCookie, setCookie } from '../../utils/cookies';
import { getApiErrorMessage } from '../../utils/apiError';

const Pedidos: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();

  // Obtener el primer y último día del mes actual
  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };

  const getLastDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  };

  const [items, setItems] = useState<PedidoVenta[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<string>('');
  const [filtroPendientesFacturar, setFiltroPendientesFacturar] = useState<boolean>(() => {
    const cookieValue = getCookie('pedidos_filtro_pendientes_facturar');
    return cookieValue === 'true';
  });
  const [fechaDesde, setFechaDesde] = useState<string>(() => {
    const fecha = getFirstDayOfMonth();
    return fecha.toISOString().split('T')[0];
  });
  const [fechaHasta, setFechaHasta] = useState<string>(() => {
    const fecha = getLastDayOfMonth();
    return fecha.toISOString().split('T')[0];
  });
  const [detalleDialogOpen, setDetalleDialogOpen] = useState(false);
  const [selectedPedidoId, setSelectedPedidoId] = useState<number | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedPedido, setSelectedPedido] = useState<number | null>(null);
  const [pdfLoading, setPdfLoading] = useState<number | null>(null);

  const fetchPedidos = useCallback(async (
    pageNumber: number, 
    pageSize: number, 
    search: string,
    estado: string,
    desde: string,
    hasta: string,
    pendientesFacturar: boolean
  ) => {
    try {
      // Asegurar que pageSize esté entre 1 y 20 (límite del backend)
      const validPageSize = Math.max(1, Math.min(20, pageSize));
      
      const params: any = {
        pageNumber: pageNumber + 1,
        pageSize: validPageSize,
        searchTerm: search || undefined,
        fechaDesde: desde ? new Date(desde) : undefined,
        fechaHasta: hasta ? new Date(hasta) : undefined,
        PendienteFacturar: pendientesFacturar || undefined
      };
      
      // Manejar filtro de estado
      if (estado) {
        params.estado = estado;
      }
      
      const response = await pedidoVentaService.getPedidosVenta(params);
      setItems(response.items);
      setTotalRecords(response.totalCount);
    } catch (error: any) {
      const message = getApiErrorMessage(error, 'Error al cargar los pedidos');
      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchPedidos(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroPendientesFacturar);
  }, [page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroPendientesFacturar, fetchPedidos]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    // Asegurar que el valor esté entre 1 y 20 (límite del backend)
    const validRowsPerPage = Math.max(1, Math.min(20, newRowsPerPage));
    setRowsPerPage(validRowsPerPage);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleNuevoPedido = () => {
    navigate('/ventas/pedidos/nuevo');
  };

  const handlePedidoClick = (pedidoId: number) => {
    navigate(`/ventas/pedidos/${pedidoId}/ver`);
  };

  const handleCloseDetalle = () => {
    setDetalleDialogOpen(false);
    setSelectedPedidoId(null);
  };

  const handleEdit = (pedidoId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/ventas/pedidos/${pedidoId}/editar`);
  };

  const handleFacturar = (pedidoId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/ventas/facturas/nuevo?pedidoId=${pedidoId}`);
  };

  const handleMarcarImpreso = async (pedidoId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await pedidoVentaService.marcarImpreso(pedidoId);
      enqueueSnackbar('Pedido marcado como impreso', { variant: 'success' });
      fetchPedidos(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroPendientesFacturar);
    } catch (error: any) {
      const message = error?.response?.data || 'Error al marcar el pedido como impreso';
      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
    }
  };

  const handleMarcarListoPicking = async (pedidoId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await pedidoVentaService.marcarListoPicking(pedidoId);
      enqueueSnackbar('Pedido enviado a picking', { variant: 'success' });
      fetchPedidos(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroPendientesFacturar);
    } catch (error: any) {
      const message = error?.response?.data || 'Error al enviar el pedido a picking';
      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
    }
  };

  const handleMarcarPreparado = async (pedidoId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await pedidoVentaService.marcarPreparado(pedidoId);
      enqueueSnackbar('Pedido marcado como preparado', { variant: 'success' });
      fetchPedidos(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroPendientesFacturar);
    } catch (error: any) {
      const message = error?.response?.data || 'Error al marcar el pedido como preparado';
      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
    }
  };

  const handleAnular = async (pedidoId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    if (window.confirm('¿Está seguro que desea anular este pedido?')) {
      try {
        await pedidoVentaService.anularPedido(pedidoId);
        enqueueSnackbar('Pedido anulado correctamente', { variant: 'success' });
        fetchPedidos(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroPendientesFacturar);
      } catch (error: any) {
        const message = error?.response?.data || 'Error al anular el pedido';
        enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
      }
    }
  };

  const handleImprimirPDF = async (pedidoId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      setPdfLoading(pedidoId);
      const blob = await pedidoVentaService.getPedidoVentaPDF(pedidoId);
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setSelectedPedido(pedidoId);
    } catch (error: any) {
      const message = error?.response?.data || 'Error al generar el PDF del pedido';
      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
    } finally {
      setPdfLoading(null);
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setSelectedPedido(null);
  };


  // Función helper para calcular el total de un pedido
  const calcularTotal = (pedido: PedidoVenta): number => {
    // Usar importeTotal directamente del API si está presente (incluso si es 0)
    if (pedido.importeTotal !== undefined && pedido.importeTotal !== null) {
      return pedido.importeTotal;
    }
    // Si no, calcular sumando los subtotales de los items como fallback
    // Si el subtotal no está presente, calcularlo como precioUnitario * cantidad
    const totalCalculado = pedido.detalles?.reduce((sum, d) => {
      const subtotal = d.subtotal || (d.precioUnitario || 0) * (d.cantidad || 0);
      return sum + subtotal;
    }, 0) || 0;
    return totalCalculado;
  };

  // Función para determinar si un pedido es editable
  // Se pueden editar pedidos en estados "Nuevo", "Impreso" y "Listo para picking"
  const esPedidoEditable = (pedido: PedidoVenta): boolean => {
    const estado = pedido.estado || 'Nuevo';
    // Estados editables: Nuevo, Impreso, Listo para picking (backend devuelve "Listo para picking")
    return estado === 'Nuevo' || estado === 'Impreso' || estado === 'Listo para picking';
  };

  // Función para verificar si un pedido está facturado
  const esPedidoFacturado = (pedido: PedidoVenta): boolean => {
    return pedido.estado === 'Facturado';
  };

  // Función para verificar si un pedido puede ser facturado
  // No se puede facturar si está facturado o si está "listo para pickear"
  const puedeFacturar = (pedido: PedidoVenta): boolean => {
    return pedido.estado !== 'Facturado' && pedido.estado !== 'Listo para pickear';
  };

  // Función para verificar si un pedido permite acciones (no facturado y no anulado)
  const permiteAcciones = (pedido: PedidoVenta): boolean => {
    return pedido.estado !== 'Facturado' && pedido.estado !== 'Anulado';
  };

  // Función para obtener el color del chip según el estado
  // Ahora usa directamente el campo 'estado' que viene del backend
  const getEstadoChip = (pedido: PedidoVenta) => {
    const estado = pedido.estado || 'Nuevo';
    
    // Mapeo de estados a colores
    const estadoMap: Record<string, { label: string; bgColor: string; textColor: string }> = {
      'Facturado': {
        label: 'Facturado',
        bgColor: '#C6EEBB', // Verde
        textColor: '#000000'
      },
      'Anulado': {
        label: 'Anulado',
        bgColor: '#FFCDD2', // Rojo suave
        textColor: '#000000'
      },
      'Impreso': {
        label: 'Impreso',
        bgColor: '#FF80FF', // Rosa
        textColor: '#000000'
      },
      'Listo para pickear': {
        label: 'Listo para pickear',
        bgColor: '#FF8C00', // Naranja
        textColor: '#000000'
      },
      'Preparado': {
        label: 'Preparado',
        bgColor: '#FFFF00', // Amarillo
        textColor: '#000000'
      },
      'Nuevo': {
        label: 'Nuevo',
        bgColor: '#FFFFD7', // Blanco/Amarillo claro
        textColor: '#000000'
      }
    };
    
    // Retornar el estado mapeado o el estado por defecto
    return estadoMap[estado] || {
      label: estado,
      bgColor: '#FFFFD7', // Blanco/Amarillo claro por defecto
      textColor: '#000000'
    };
  };

  const formatComprobante = (pedido: PedidoVenta & { letra?: string; nroSuc?: number; nroComprobante?: number }) => {
    const letra = (pedido as any).letra;
    const nroSuc = (pedido as any).nroSuc;
    const nroComprobante = (pedido as any).nroComprobante;
    
    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) {
      return '-';
    }

    const formatPart = (value: number | string, length: number) => {
      if (value === null || value === undefined) {
        return ''.padStart(length, '0');
      }
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return numeric.toString().padStart(length, '0');
      }
      return String(value).padStart(length, '0');
    };

    const sucursal = formatPart(nroSuc, 5);
    const comprobante = formatPart(nroComprobante, 8);
    const letraNormalizada = letra.trim().toUpperCase();

    if (!letraNormalizada) {
      return '-';
    }

    return `${letraNormalizada}-${sucursal}-${comprobante}`;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Tarjeta blanca contenedora principal */}
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header con título y botones */}
          <Stack 
            direction="row" 
            justifyContent="space-between" 
            alignItems="center" 
            sx={{ mb: 3 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Pedidos
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleNuevoPedido}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                }
              }}
            >
              Nuevo Pedido
            </Button>
          </Stack>

          {/* Barra de búsqueda y filtros */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Buscar por número, descripción, nombre o CUIT..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={filtroEstado}
                    label="Estado"
                    onChange={(e) => {
                      setFiltroEstado(e.target.value);
                      setPage(0);
                    }}
                    sx={{
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="Nuevo">Nuevo</MenuItem>
                    <MenuItem value="Impreso">Impreso</MenuItem>
                    <MenuItem value="Listo para pickear">Listo para pickear</MenuItem>
                    <MenuItem value="Preparado">Preparado</MenuItem>
                    <MenuItem value="Facturado">Facturado</MenuItem>
                    <MenuItem value="Anulado">Anulado</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filtroPendientesFacturar}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setFiltroPendientesFacturar(newValue);
                        setCookie('pedidos_filtro_pendientes_facturar', newValue.toString());
                        setPage(0);
                      }}
                    />
                  }
                  label="Pend. de Facturar"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Tabla de pedidos */}
          {isMobile ? (
            <Box>
              {items.map((pedido) => {
                const estadoChip = getEstadoChip(pedido);
                return (
                  <Card
                    key={pedido.nroFormulario}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Pedido #{pedido.nroFormulario}
                          </Typography>
                          <Chip
                            label={estadoChip.label}
                            size="small"
                            sx={{
                              bgcolor: estadoChip.bgColor,
                              color: estadoChip.textColor,
                              fontWeight: 500,
                              borderRadius: 1.5,
                            }}
                          />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          Fecha: {new Date(pedido.fechaEmision).toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Cliente: {pedido.nroCliente} - {pedido.nombre}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          Importe Total: {formatCurrency(calcularTotal(pedido))}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Usuario: {pedido.usuario}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Tooltip title="Ver detalle">
                            <IconButton
                              size="small"
                              onClick={() => handlePedidoClick(pedido.nroFormulario)}
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#1976d2' }
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {esPedidoEditable(pedido) && (
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={(e) => handleEdit(pedido.nroFormulario, e)}
                                sx={{
                                  bgcolor: '#ff9800',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  '&:hover': { bgcolor: '#f57c00' }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {permiteAcciones(pedido) && (
                            <>
                              {pedido.estado !== 'Preparado' && pedido.estado !== 'Listo para pickear' && (
                                <>
                                  <Tooltip title="Imprimir PDF">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleImprimirPDF(pedido.nroFormulario, e)}
                                      disabled={pdfLoading === pedido.nroFormulario}
                                      sx={{
                                        bgcolor: '#2196f3',
                                        color: '#fff',
                                        borderRadius: 1.5,
                                        '&:hover': { bgcolor: '#1976d2' },
                                        '&:disabled': { bgcolor: '#ccc' }
                                      }}
                                    >
                                      {pdfLoading === pedido.nroFormulario ? (
                                        <CircularProgress size={16} sx={{ color: '#fff' }} />
                                      ) : (
                                        <PrintIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                  {pedido.estado !== 'Impreso' && (
                                    <Tooltip title="Marcar como impreso">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => handleMarcarImpreso(pedido.nroFormulario, e)}
                                        sx={{
                                          bgcolor: '#FF80FF',
                                          color: '#fff',
                                          borderRadius: 1.5,
                                          '&:hover': { bgcolor: '#FF60FF' }
                                        }}
                                      >
                                        <PrintIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <Tooltip title="Enviar a picking">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleMarcarListoPicking(pedido.nroFormulario, e)}
                                      sx={{
                                        bgcolor: '#FF8C00',
                                        color: '#fff',
                                        borderRadius: 1.5,
                                        '&:hover': { bgcolor: '#FF7A00' }
                                      }}
                                    >
                                      <InventoryIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                              {pedido.estado !== 'Preparado' && (
                                <Tooltip title="Marcar preparado">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleMarcarPreparado(pedido.nroFormulario, e)}
                                    sx={{
                                      bgcolor: '#FFFF00',
                                      color: '#000',
                                      borderRadius: 1.5,
                                      '&:hover': { bgcolor: '#FFE600' }
                                    }}
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {puedeFacturar(pedido) && (
                                <Tooltip title="Facturar pedido">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleFacturar(pedido.nroFormulario, e)}
                                    sx={{
                                      bgcolor: '#4caf50',
                                      color: '#fff',
                                      borderRadius: 1.5,
                                      width: 32,
                                      height: 32,
                                      '&:hover': { 
                                        bgcolor: '#45a049',
                                        transform: 'scale(1.1)',
                                      },
                                      transition: 'all 0.2s'
                                    }}
                                  >
                                    <ReceiptIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Anular pedido">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleAnular(pedido.nroFormulario, e)}
                                  sx={{
                                    bgcolor: '#f44336',
                                    color: '#fff',
                                    borderRadius: 1.5,
                                    '&:hover': { bgcolor: '#d32f2f' }
                                  }}
                                >
                                  <BlockIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {pedido.estado === 'Facturado' && (
                            <Tooltip title="Imprimir PDF">
                              <IconButton
                                size="small"
                                onClick={(e) => handleImprimirPDF(pedido.nroFormulario, e)}
                                disabled={pdfLoading === pedido.nroFormulario}
                                sx={{
                                  bgcolor: '#2196f3',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  '&:hover': { bgcolor: '#1976d2' },
                                  '&:disabled': { bgcolor: '#ccc' }
                                }}
                              >
                                {pdfLoading === pedido.nroFormulario ? (
                                  <CircularProgress size={16} sx={{ color: '#fff' }} />
                                ) : (
                                  <PrintIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
              />
            </Box>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Nro. Pedido</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' }, minWidth: 110 }}>Comprobante</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Importe Total</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Usuario</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((pedido) => {
                    const estadoChip = getEstadoChip(pedido);
                    return (
                      <TableRow
                        key={pedido.nroFormulario}
                        sx={{
                          '&:hover': {
                            bgcolor: '#f9f9f9',
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>
                          {pedido.nroFormulario}
                        </TableCell>
                        <TableCell>
                          {new Date(pedido.fechaEmision).toLocaleDateString()}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          {formatComprobante(pedido)}
                        </TableCell>
                        <TableCell>{pedido.nroCliente}</TableCell>
                        <TableCell>{pedido.nombre}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          {formatCurrency(calcularTotal(pedido))}
                        </TableCell>
                        <TableCell>{pedido.usuario}</TableCell>
                        <TableCell>
                          <Chip
                            label={estadoChip.label}
                            size="small"
                            sx={{
                              bgcolor: estadoChip.bgColor,
                              color: estadoChip.textColor,
                              fontWeight: 500,
                              borderRadius: 1.5,
                              minWidth: 90,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Ver detalle">
                              <IconButton
                                size="small"
                                onClick={() => handlePedidoClick(pedido.nroFormulario)}
                                sx={{
                                  bgcolor: '#2196f3',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  width: 32,
                                  height: 32,
                                  '&:hover': { 
                                    bgcolor: '#1976d2',
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {esPedidoEditable(pedido) && (
                              <Tooltip title="Editar">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleEdit(pedido.nroFormulario, e)}
                                  sx={{
                                    bgcolor: '#ff9800',
                                    color: '#fff',
                                    borderRadius: 1.5,
                                    width: 32,
                                    height: 32,
                                    '&:hover': { 
                                      bgcolor: '#f57c00',
                                      transform: 'scale(1.1)',
                                    },
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {permiteAcciones(pedido) && (
                              <>
                                {pedido.asentado !== 'P' && pedido.asentado !== 'K' && (
                                  <>
                                    <Tooltip title="Imprimir PDF">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => handleImprimirPDF(pedido.nroFormulario, e)}
                                        disabled={pdfLoading === pedido.nroFormulario}
                                        sx={{
                                          bgcolor: '#2196f3',
                                          color: '#fff',
                                          borderRadius: 1.5,
                                          width: 32,
                                          height: 32,
                                          '&:hover': { 
                                            bgcolor: '#1976d2',
                                            transform: 'scale(1.1)',
                                          },
                                          '&:disabled': { bgcolor: '#ccc' },
                                          transition: 'all 0.2s'
                                        }}
                                      >
                                        {pdfLoading === pedido.nroFormulario ? (
                                          <CircularProgress size={16} sx={{ color: '#fff' }} />
                                        ) : (
                                          <PrintIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                    {pedido.asentado !== 'I' && (
                                      <Tooltip title="Marcar como impreso">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => handleMarcarImpreso(pedido.nroFormulario, e)}
                                          sx={{
                                            bgcolor: '#FF80FF',
                                            color: '#fff',
                                            borderRadius: 1.5,
                                            width: 32,
                                            height: 32,
                                            '&:hover': { 
                                              bgcolor: '#FF60FF',
                                              transform: 'scale(1.1)',
                                            },
                                            transition: 'all 0.2s'
                                          }}
                                        >
                                          <PrintIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title="Enviar a picking">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => handleMarcarListoPicking(pedido.nroFormulario, e)}
                                        sx={{
                                          bgcolor: '#FF8C00',
                                          color: '#fff',
                                          borderRadius: 1.5,
                                          width: 32,
                                          height: 32,
                                          '&:hover': { 
                                            bgcolor: '#FF7A00',
                                            transform: 'scale(1.1)',
                                          },
                                          transition: 'all 0.2s'
                                        }}
                                      >
                                        <InventoryIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                                {pedido.estado !== 'Preparado' && (
                                  <Tooltip title="Marcar preparado">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleMarcarPreparado(pedido.nroFormulario, e)}
                                      sx={{
                                        bgcolor: '#FFFF00',
                                        color: '#000',
                                        borderRadius: 1.5,
                                        width: 32,
                                        height: 32,
                                        '&:hover': { 
                                          bgcolor: '#FFE600',
                                          transform: 'scale(1.1)',
                                        },
                                        transition: 'all 0.2s'
                                      }}
                                    >
                                      <CheckCircleIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {puedeFacturar(pedido) && (
                                  <Tooltip title="Facturar pedido">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleFacturar(pedido.nroFormulario, e)}
                                      sx={{
                                        bgcolor: '#4caf50',
                                        color: '#fff',
                                        borderRadius: 1.5,
                                        width: 32,
                                        height: 32,
                                        '&:hover': { 
                                          bgcolor: '#45a049',
                                          transform: 'scale(1.1)',
                                        },
                                        transition: 'all 0.2s'
                                      }}
                                    >
                                      <ReceiptIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Anular pedido">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleAnular(pedido.nroFormulario, e)}
                                    sx={{
                                      bgcolor: '#f44336',
                                      color: '#fff',
                                      borderRadius: 1.5,
                                      width: 32,
                                      height: 32,
                                      '&:hover': { 
                                        bgcolor: '#d32f2f',
                                        transform: 'scale(1.1)',
                                      },
                                      transition: 'all 0.2s'
                                    }}
                                  >
                                    <BlockIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            {pedido.estado === 'Facturado' && (
                              <Tooltip title="Imprimir PDF">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleImprimirPDF(pedido.nroFormulario, e)}
                                  disabled={pdfLoading === pedido.nroFormulario}
                                  sx={{
                                    bgcolor: '#2196f3',
                                    color: '#fff',
                                    borderRadius: 1.5,
                                    width: 32,
                                    height: 32,
                                    '&:hover': { 
                                      bgcolor: '#1976d2',
                                      transform: 'scale(1.1)',
                                    },
                                    '&:disabled': { bgcolor: '#ccc' },
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  {pdfLoading === pedido.nroFormulario ? (
                                    <CircularProgress size={16} sx={{ color: '#fff' }} />
                                  ) : (
                                    <PrintIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                sx={{
                  borderTop: '1px solid #e0e0e0',
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <PedidoDetalle
        open={detalleDialogOpen}
        onClose={handleCloseDetalle}
        pedidoId={selectedPedidoId}
      />

      {pdfUrl && selectedPedido && (
        <PDFViewer 
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Pedido #${selectedPedido}`}
          documentId={`${selectedPedido}`}
          documentType="pedido"
        />
      )}
    </Box>
  );
};

export default Pedidos;




